export const THEME_COLORS = [
	"theme-color-blue",
	"theme-color-green",
	"theme-color-red",
	"theme-color-gold",
	"",
] as const;

export const THEME_MODES = ["theme-dark", "theme-light"] as const;

export type ThemeColor = (typeof THEME_COLORS)[number];
export type ThemeMode = (typeof THEME_MODES)[number];

const THEME_COLOR_SET = new Set<string>(THEME_COLORS);
const THEME_MODE_SET = new Set<string>(THEME_MODES);

export function isThemeColor(value: unknown): value is ThemeColor {
	return typeof value === "string" && THEME_COLOR_SET.has(value);
}

export function isThemeMode(value: unknown): value is ThemeMode {
	return typeof value === "string" && THEME_MODE_SET.has(value);
}

export type ThemeChangeMessage = {
	type: "theme-change";
	colorTheme: ThemeColor;
	modeTheme: ThemeMode;
};

export type VisitorCountMessage = {
	type: "visitor-count";
	count: number;
};

export type SetThemeMessage = {
	type: "set-theme";
	colorTheme: ThemeColor;
	modeTheme: ThemeMode;
};

export type ErrorMessage = {
	type: "error";
	message: string;
};

export type OutgoingMessage = ThemeChangeMessage | VisitorCountMessage;
export type IncomingMessage = SetThemeMessage;
export type ServerMessage = OutgoingMessage | ErrorMessage;

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
	return typeof value === "object" && value !== null;
}

export function createThemeChangeMessage(
	colorTheme: ThemeColor,
	modeTheme: ThemeMode,
): ThemeChangeMessage {
	return {
		type: "theme-change",
		colorTheme,
		modeTheme,
	};
}

export function createVisitorCountMessage(count: number): VisitorCountMessage {
	return {
		type: "visitor-count",
		count,
	};
}

export function createErrorMessage(message: string): ErrorMessage {
	return {
		type: "error",
		message,
	};
}

export function parseIncomingMessage(value: unknown): IncomingMessage | null {
	if (!isRecord(value) || value.type !== "set-theme") {
		return null;
	}

	if (!isThemeColor(value.colorTheme) || !isThemeMode(value.modeTheme)) {
		return null;
	}

	return {
		type: "set-theme",
		colorTheme: value.colorTheme,
		modeTheme: value.modeTheme,
	};
}

export function parseServerMessage(value: unknown): ServerMessage | null {
	if (!isRecord(value) || typeof value.type !== "string") {
		return null;
	}

	switch (value.type) {
		case "theme-change":
			if (!isThemeColor(value.colorTheme) || !isThemeMode(value.modeTheme)) {
				return null;
			}

			return createThemeChangeMessage(value.colorTheme, value.modeTheme);
		case "visitor-count":
			if (typeof value.count !== "number") {
				return null;
			}

			return createVisitorCountMessage(value.count);
		case "error":
			if (typeof value.message !== "string") {
				return null;
			}

			return createErrorMessage(value.message);
		default:
			return null;
	}
}
